/**
 * Calorie Output Estimator
 *
 * Pure calculation module — no DB calls, no side effects.
 * Estimates energy expenditure for all 9 exercise modalities using
 * MET-based or distance-based formulas as appropriate.
 *
 * Sources: Compendium of Physical Activities (Ainsworth 2011),
 * ACSM metabolic equations, Pandolf load-carriage equation.
 */

import type { ExerciseLog, SessionLog, PTSessionLog, SprintSession } from './sessionStorage';
import { computeSetVolume } from './volumeTracking';
import type {
  CalorieEstimate,
  SessionCalorieSummary,
  DailyCalorieSummary,
  METContextData,
} from './types/activity';

// ============================================================================
// CONSTANTS
// ============================================================================

export const CALORIE_VERSION = 2;
const LBS_TO_KG = 0.453592;
const DEFAULT_RPE = 7;
const DEFAULT_BODY_WEIGHT_LBS = 180;

// Pandolf equation terrain coefficients
const TERRAIN_PAVED = 1.0;   // η (eta) for paved/hard surface

/**
 * Pandolf Load-Carriage Equation (1977, updated Santee 2001)
 * M = 1.5·W + 2.0·(W + L)·(L/W)² + η·(W + L)·(1.5·V² + 0.35·V·G)
 *
 * Where: M = metabolic rate (watts), W = body mass (kg), L = external load (kg),
 *        V = walking speed (m/s), G = grade (%), η = terrain coefficient
 *
 * Returns kcal for the given duration.
 */
function pandolfCalories(
  bodyKg: number,
  loadKg: number,
  speedMs: number,
  gradePercent: number,
  durationSeconds: number,
  terrain: number = TERRAIN_PAVED,
): number {
  const W = bodyKg;
  const L = loadKg;
  const V = Math.max(0.5, speedMs); // floor at slow walk to avoid div/0
  const G = Math.max(0, gradePercent); // negative grade not modeled

  // Metabolic rate in watts
  const M = 1.5 * W
    + 2.0 * (W + L) * Math.pow(L / W, 2)
    + terrain * (W + L) * (1.5 * Math.pow(V, 2) + 0.35 * V * G);

  // Watts → kcal/s = watts / 4184 * 1000 = watts * 0.000239
  // Actually: 1 watt = 0.860 kcal/hour, so kcal/s = watts * 0.860 / 3600
  const kcalPerSecond = M * 0.860 / 3600;
  return Math.max(0, kcalPerSecond * durationSeconds);
}

/**
 * ACSM Running Metabolic Equation
 * VO2 (mL/kg/min) = 0.2·S + 0.9·S·G + 3.5
 * Where S = speed (m/min), G = fractional grade (0.05 = 5%)
 * kcal/min = VO2 · bodyKg / 1000 · 5.0 (5 kcal per L O2)
 */
function acsmRunningCalories(bodyKg: number, speedMperMin: number, grade: number, durationMin: number): number {
  const S = Math.max(26.8, speedMperMin); // min ~1 mph threshold
  const G = Math.max(0, grade);
  const vo2 = 0.2 * S + 0.9 * S * G + 3.5;
  const kcalPerMin = (vo2 * bodyKg) / 1000 * 5.0;
  return kcalPerMin * durationMin;
}

/**
 * ACSM Walking Metabolic Equation
 * VO2 (mL/kg/min) = 0.1·S + 1.8·S·G + 3.5
 * Applicable for speeds 50-100 m/min (~1.9-3.7 mph)
 */
function acsmWalkingCalories(bodyKg: number, speedMperMin: number, grade: number, durationMin: number): number {
  const S = Math.max(26.8, speedMperMin);
  const G = Math.max(0, grade);
  const vo2 = 0.1 * S + 1.8 * S * G + 3.5;
  const kcalPerMin = (vo2 * bodyKg) / 1000 * 5.0;
  return kcalPerMin * durationMin;
}

/**
 * MET values by exercise type and RPE intensity bracket.
 * RPE brackets: low (1-4), moderate (5-6), hard (7-8), max (9-10)
 */
const MET_TABLE: Record<string, [number, number, number, number]> = {
  // [low, moderate, hard, max]
  weight:     [3.5, 5.0, 6.0, 6.5],
  bodyweight: [3.8, 5.0, 8.0, 9.5],
  circuit:    [4.3, 6.0, 8.0, 9.5],
  timed:      [3.0, 3.5, 4.0, 4.5],
  carry:      [5.0, 6.5, 8.0, 10.0],
  interval:   [8.5, 9.0, 10.5, 12.0],
};

/** MET for range (shooting drills) — standing, low intensity */
const RANGE_MET = 2.5;

/** Minimum kcal per kg of tonnage (cross-validation floor for strength) */
const TONNAGE_KCAL_PER_KG = 0.045;

// ============================================================================
// EPOC (Excess Post-Exercise Oxygen Consumption) MULTIPLIERS
// ============================================================================

/**
 * EPOC multiplier ranges by exercise category.
 * [minMultiplier, maxMultiplier] — interpolated by RPE.
 * Sources: Borsheim & Bahr 2003, Knab et al. 2011
 */
const EPOC_MULTIPLIERS: Record<string, [number, number]> = {
  strength: [1.06, 1.10],   // Moderate EPOC from resistance training
  hiit:     [1.10, 1.15],   // Highest EPOC from high-intensity intervals
  circuit:  [1.08, 1.12],   // Hybrid EPOC from circuit/metabolic conditioning
};

/**
 * Classify session exercises into an EPOC category.
 */
function classifyEPOCCategory(exercises: ExerciseLog[]): keyof typeof EPOC_MULTIPLIERS | null {
  if (exercises.length === 0) return null;

  let hasInterval = false;
  let hasCircuit = false;
  let hasStrength = false;

  for (const ex of exercises) {
    if (ex.exerciseType === 'interval' || ex.exerciseType === 'cardio') hasInterval = true;
    else if (ex.exerciseType === 'circuit') hasCircuit = true;
    else if (ex.exerciseType === 'weight' || ex.exerciseType === 'bodyweight') hasStrength = true;
  }

  // Prioritize highest EPOC category present
  if (hasInterval) return 'hiit';
  if (hasCircuit) return 'circuit';
  if (hasStrength) return 'strength';
  return null;
}

/**
 * Calculate EPOC multiplier based on exercise types and average RPE.
 * Interpolates between min/max based on RPE 7-10 range.
 * Returns 1.0 (no EPOC) for low-intensity work (RPE < 7).
 */
export function calculateEPOCMultiplier(exercises: ExerciseLog[], avgRPE: number): number {
  const category = classifyEPOCCategory(exercises);
  if (!category) return 1.0;

  // EPOC is negligible below RPE 7
  if (avgRPE < 7) return 1.0;

  const [min, max] = EPOC_MULTIPLIERS[category];
  // Linear interpolation: RPE 7 = min, RPE 10 = max
  const t = Math.min(1, Math.max(0, (avgRPE - 7) / 3));
  return min + t * (max - min);
}

// ============================================================================
// HELPERS
// ============================================================================

export function lbsToKg(lbs: number): number {
  return lbs * LBS_TO_KG;
}

function parseTimeToSeconds(time?: string): number {
  if (!time) return 0;
  const parts = time.split(':').map(p => p.trim()).filter(Boolean);
  if (parts.length === 0) return 0;
  const nums = parts.map(p => Number(p));
  if (nums.some(n => Number.isNaN(n))) return 0;
  if (nums.length === 2) return Math.max(0, nums[0] * 60 + nums[1]);
  if (nums.length === 3) return Math.max(0, nums[0] * 3600 + nums[1] * 60 + nums[2]);

  /**
   * A BARE NUMBER IS MINUTES.
   *
   * `actualTime` is documented as MM:SS, but the cardio/swim UI has been
   * writing plain integers for a long time. Production carries "30", "20" and
   * "12" alongside "3:00" and "09:10" — and a one-part split fell through
   * both branches and returned ZERO. Measured: 18 swim/interval logs, every
   * one with no sets, no rounds and no distance, so a duration of 0 made the
   * entire session invisible to volume, training load, ACWR and calories.
   *
   * Minutes, not seconds: these are whole-bout durations for a swim or track
   * session. Reading "20" as twenty seconds would be a 20-second swim.
   */
  if (nums.length === 1) {
    const v = nums[0];
    return v > 0 ? Math.round(v * 60) : 0;
  }
  return 0;
}

/**
 * Extract average RPE from an exercise log.
 * Checks per-set, per-round, per-swim-set, then legacy single-value.
 */
export function getExerciseRPE(ex: ExerciseLog): number {
  if (ex.sets && ex.sets.length > 0) {
    const rpes = ex.sets.map(s => s.rpe).filter((r): r is number => typeof r === 'number' && r > 0);
    if (rpes.length > 0) return rpes.reduce((a, b) => a + b, 0) / rpes.length;
  }
  if (ex.circuitSets && ex.circuitSets.length > 0) {
    const rpes = ex.circuitSets.map(s => s.rpe).filter((r): r is number => typeof r === 'number' && r > 0);
    if (rpes.length > 0) return rpes.reduce((a, b) => a + b, 0) / rpes.length;
  }
  if (ex.swimSets && ex.swimSets.length > 0) {
    const rpes = ex.swimSets.map(s => s.rpe).filter((r): r is number => typeof r === 'number' && r > 0);
    if (rpes.length > 0) return rpes.reduce((a, b) => a + b, 0) / rpes.length;
  }
  if (typeof ex.actualRPE === 'number' && ex.actualRPE > 0) return ex.actualRPE;
  return DEFAULT_RPE;
}

/**
 * Map RPE (1-10) to a MET table index (0-3).
 *   1-4 → 0 (low), 5-6 → 1 (moderate), 7-8 → 2 (hard), 9-10 → 3 (max)
 */
function rpeToBracket(rpe: number): number {
  if (rpe <= 4) return 0;
  if (rpe <= 6) return 1;
  if (rpe <= 8) return 2;
  return 3;
}

/**
 * Look up MET value for a given exercise type and RPE.
 */
export function getExerciseMET(exerciseType: string, rpe: number, _context?: METContextData): number {
  const bracket = rpeToBracket(rpe);
  const row = MET_TABLE[exerciseType];
  if (row) return row[bracket];
  // Fallback: treat unknown types as moderate bodyweight
  return MET_TABLE.bodyweight[bracket];
}

// ============================================================================
// DURATION ESTIMATION
// ============================================================================

/**
 * Estimate exercise duration in seconds from logged data.
 * Uses actual time when available, otherwise infers from sets/reps/rest.
 */
export function estimateExerciseDuration(ex: ExerciseLog): number {
  // Explicit time logged
  if (ex.actualTime) {
    const seconds = parseTimeToSeconds(ex.actualTime);
    if (seconds > 0) return seconds;
  }

  const type = ex.exerciseType;

  // Weight: completedSets x (repsPerSet x 4sec + restSeconds)
  if (type === 'weight') {
    if (ex.sets && ex.sets.length > 0) {
      const completedSets = ex.sets.filter(s => s.completed);
      const restSeconds = parseRestPeriod(ex.restPeriod) || 90;
      return completedSets.reduce((total, set) => {
        return total + (set.reps * 4) + restSeconds;
      }, 0);
    }
    // Legacy
    const numSets = parseLeadingInt(ex.targetSets) || 3;
    const reps = ex.actualReps || 8;
    const rest = parseRestPeriod(ex.restPeriod) || 90;
    return numSets * (reps * 4 + rest);
  }

  // Bodyweight: totalReps x 3sec + (sets - 1) x 60sec rest
  if (type === 'bodyweight') {
    if (ex.sets && ex.sets.length > 0) {
      const completedSets = ex.sets.filter(s => s.completed);
      const totalReps = completedSets.reduce((sum, s) => sum + s.reps, 0);
      return totalReps * 3 + Math.max(0, completedSets.length - 1) * 60;
    }
    const reps = ex.actualReps || 20;
    const numSets = parseLeadingInt(ex.targetSets) || 3;
    return reps * numSets * 3 + Math.max(0, numSets - 1) * 60;
  }

  // Circuit: sum circuitSets[].time or rounds x 120sec
  if (type === 'circuit') {
    if (ex.circuitSets && ex.circuitSets.length > 0) {
      const totalFromTimes = ex.circuitSets.reduce((sum, cs) => {
        return sum + (parseTimeToSeconds(cs.time) || 120);
      }, 0);
      if (totalFromTimes > 0) return totalFromTimes;
    }
    const rounds = ex.actualRounds || parseLeadingInt(ex.targetSets) || 4;
    return rounds * 120;
  }

  // Carry: distance / 1.2 m/s or actual time
  if (type === 'carry') {
    if (ex.actualDistance && ex.actualDistance > 0) {
      return Math.round(ex.actualDistance / 1.2);
    }
    return 120; // 2 min default for carries
  }

  // Interval: intervalSets.length x 90sec
  if (type === 'interval') {
    if (ex.intervalSets && ex.intervalSets.length > 0) {
      return ex.intervalSets.length * 90;
    }
    return 300; // 5 min default
  }

  // Swim: sum swim set times
  if (type === 'swim-sets') {
    if (ex.swimSets && ex.swimSets.length > 0) {
      const totalTime = ex.swimSets.reduce((sum, ss) => sum + parseTimeToSeconds(ss.time), 0);
      if (totalTime > 0) return totalTime;
    }
    return 300;
  }

  // Cardio: use actual time or estimate from distance
  if (type === 'cardio') {
    if (ex.actualDistance && ex.actualDistance > 0) {
      // Assume ~3 m/s jogging pace if no time
      return Math.round(ex.actualDistance / 3);
    }
    return 300;
  }

  // Ruck: similar to cardio but slower pace (~1.5 m/s)
  if (type === 'ruck') {
    if (ex.actualDistance && ex.actualDistance > 0) {
      return Math.round(ex.actualDistance / 1.5);
    }
    return 600; // 10 min default
  }

  // Timed (holds, planks): use actual time or default 60sec
  if (type === 'timed') {
    return 60;
  }

  return 120; // Generic fallback
}

function parseRestPeriod(rest?: string): number {
  if (!rest) return 0;
  // MINUTES must be handled before the bare-number fallback. Programs author
  // rest as "2-3 min" as well as "90s", and `parseInt("2-3 min")` returned 2 —
  // read as 2 SECONDS of rest instead of ~150. The session UI parses the same
  // string correctly, so the two disagreed on identical input.
  const minutes = rest.match(/(\d+(?:\.\d+)?)\s*(?:-\s*(\d+(?:\.\d+)?)\s*)?m(?:in)?\b/i);
  if (minutes) {
    const lo = parseFloat(minutes[1]);
    const hi = minutes[2] ? parseFloat(minutes[2]) : lo;
    return Math.round(((lo + hi) / 2) * 60);
  }
  // Handle formats like "90s", "2:00", "90"
  const seconds = parseTimeToSeconds(rest);
  if (seconds > 0) return seconds;
  const num = parseInt(rest, 10);
  return isNaN(num) ? 0 : num;
}

function parseLeadingInt(s?: string): number {
  if (!s) return 0;
  const match = s.match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

// ============================================================================
// CALORIE ESTIMATION — SINGLE EXERCISE
// ============================================================================

/**
 * Estimate calories burned for a single exercise.
 * Uses distance-based formulas for cardio/ruck/swim, MET-based for everything else.
 * Applies tonnage floor for strength exercises.
 */
export function estimateExerciseCalories(ex: ExerciseLog, bodyWeightLbs: number): CalorieEstimate {
  const bwKg = lbsToKg(bodyWeightLbs || DEFAULT_BODY_WEIGHT_LBS);
  const rpe = getExerciseRPE(ex);
  const type = ex.exerciseType;
  const durationSeconds = estimateExerciseDuration(ex);
  const durationHours = durationSeconds / 3600;

  let calories = 0;
  let method: CalorieEstimate['method'] = 'met';
  let metValue = 0;
  let confidence: CalorieEstimate['confidence'] = 'medium';

  // --- Distance-based formulas (cardio, ruck, swim) ---
  if (type === 'cardio' && ex.actualDistance && ex.actualDistance > 0) {
    // ACSM metabolic equations — walking vs running based on speed
    const speedMperMin = durationSeconds > 0
      ? (ex.actualDistance / durationSeconds) * 60
      : 134; // default ~5 mph jog
    const grade = (ex.actualIncline || 0) / 100; // convert % to fraction for ACSM
    const durationMin = durationSeconds / 60;
    // ACSM walking eq for < 100 m/min (~3.7 mph), running eq above
    if (speedMperMin < 100) {
      calories = acsmWalkingCalories(bwKg, speedMperMin, grade, durationMin);
    } else {
      calories = acsmRunningCalories(bwKg, speedMperMin, grade, durationMin);
    }
    method = 'distance';
    metValue = durationHours > 0 ? calories / (bwKg * durationHours) : 0;
    confidence = ex.actualTime ? 'high' : 'medium';
  } else if (type === 'ruck' && ex.actualDistance && ex.actualDistance > 0) {
    // Pandolf load-carriage equation — military standard for rucking
    const externalLoadKg = ex.actualLoad ? lbsToKg(ex.actualLoad) : 0;
    const speedMs = durationSeconds > 0 ? ex.actualDistance / durationSeconds : 1.34; // default ~3 mph
    const grade = ex.actualIncline || 0;
    calories = pandolfCalories(bwKg, externalLoadKg, speedMs, grade, durationSeconds);
    method = 'distance';
    metValue = durationHours > 0 ? calories / (bwKg * durationHours) : 0;
    confidence = ex.actualTime ? 'high' : 'medium';
  } else if (type === 'carry' && ex.actualDistance && ex.actualDistance > 0) {
    // Carries with distance+load: use Pandolf at slow walking speed
    // Farmer's walks / sled drags are typically ~0.9-1.1 m/s
    const externalLoadKg = ex.actualLoad ? lbsToKg(ex.actualLoad) : 0;
    const speedMs = durationSeconds > 0 ? ex.actualDistance / durationSeconds : 0.9;
    calories = pandolfCalories(bwKg, externalLoadKg, speedMs, 0, durationSeconds);
    method = 'distance';
    metValue = durationHours > 0 ? calories / (bwKg * durationHours) : 0;
    confidence = ex.actualTime ? 'high' : 'medium';
  } else if (type === 'swim-sets') {
    let totalDistMeters = 0;
    if (ex.swimSets && ex.swimSets.length > 0) {
      totalDistMeters = ex.swimSets.reduce((sum, ss) => sum + (ss.distance || 0), 0);
    } else if (ex.actualDistance) {
      totalDistMeters = ex.actualDistance;
    }
    if (totalDistMeters > 0) {
      const distKm = totalDistMeters / 1000;
      calories = bwKg * distKm * 3.0;
      method = 'distance';
      metValue = durationHours > 0 ? calories / (bwKg * durationHours) : 0;
      confidence = 'high';
    } else {
      // Fallback to MET
      metValue = getExerciseMET('bodyweight', rpe); // Swim ~= bodyweight effort
      calories = metValue * bwKg * durationHours;
      confidence = 'low';
    }
  } else {
    // --- MET-based estimation ---
    metValue = getExerciseMET(type, rpe);
    calories = metValue * bwKg * durationHours;

    // Determine confidence
    if (ex.actualTime || (ex.sets && ex.sets.some(s => s.completed && s.rpe !== null))) {
      confidence = 'high';
    } else if (ex.sets && ex.sets.length > 0) {
      confidence = 'medium';
    } else {
      confidence = 'low';
    }

    // Tonnage floor for strength exercises
    if (type === 'weight') {
      const tonnageKg = calculateTonnageKg(ex);
      const tonnageFloor = tonnageKg * TONNAGE_KCAL_PER_KG;
      if (tonnageFloor > calories) {
        calories = tonnageFloor;
        method = 'tonnage-floor';
      }
    }
  }

  /**
   * PHYSIOLOGICAL SANITY BOUND.
   *
   * The Pandolf/ACSM paths take speed = distance / duration, so a mis-scaled
   * or mis-parsed time produces an absurd speed and an absurd result — a
   * single logged exercise yielded 40,002 kcal, reported at "high" confidence
   * because a time string was present. An implausible number stated
   * confidently is worse than no number: it silently poisons daily totals and
   * every energy-balance surface built on them.
   *
   * ~25 kcal/kg/hour is roughly elite-athlete maximal output sustained for an
   * hour (~1,750 kcal/h for a 70 kg athlete). Anything above it is a data or
   * unit error, not an athletic feat, so it is clamped and demoted rather
   * than presented as fact.
   */
  const MAX_KCAL_PER_KG_HOUR = 25;
  const ceiling = bwKg * MAX_KCAL_PER_KG_HOUR * Math.max(durationHours, 1 / 60);
  if (calories > ceiling) {
    console.warn(
      `[calories] ${ex.exerciseName}: ${Math.round(calories)} kcal exceeds the ` +
      `physiological ceiling (${Math.round(ceiling)}) — clamped. Check the logged duration/distance.`,
    );
    calories = ceiling;
    confidence = 'low';
    method = 'clamped';
  }

  return {
    exerciseName: ex.exerciseName,
    exerciseType: type,
    calories: Math.round(Math.max(0, calories)),
    method,
    metValue: Math.round(metValue * 10) / 10,
    durationSeconds,
    confidence,
  };
}

/**
 * Calculate tonnage in kg from a weight exercise.
 */
function calculateTonnageKg(ex: ExerciseLog): number {
  let tonnageLbs = 0;
  if (ex.sets && ex.sets.length > 0) {
    tonnageLbs = ex.sets
      .filter(s => s.completed)
      .reduce((sum, s) => sum + computeSetVolume(s, ex.exerciseName), 0);
  } else if (ex.actualLoad && ex.actualReps) {
    const numSets = parseLeadingInt(ex.targetSets) || 1;
    tonnageLbs = ex.actualLoad * ex.actualReps * numSets;
  }
  return lbsToKg(tonnageLbs);
}

// ============================================================================
// SESSION-LEVEL ESTIMATION
// ============================================================================

/**
 * Estimate total calories for an entire session.
 */
export function estimateSessionCalories(
  session: SessionLog,
  bodyWeightLbs: number
): SessionCalorieSummary {
  const exercises: ExerciseLog[] = [];
  let sessionType = session.sessionType;

  // Match on the SHAPE, not the label. `sessionType` is not a closed set in
  // practice — 15 production sessions carry a workout NAME there instead of a
  // type ("Chassis Cut Day 3"), and those 73 logged exercises scored 0 kcal
  // purely because the string did not match one of three literals. Anything
  // carrying an `exercises` array can be estimated; only `range` is genuinely
  // a different shape.
  if (sessionType !== 'range' && 'exercises' in session && Array.isArray((session as PTSessionLog).exercises)) {
    const s = session as PTSessionLog | SprintSession;
    if (s.exercises) {
      // Deliberately NOT filtered on `ex.completed`. That flag is stamped at
      // save time as `completedSets.length === ex.sets.length` — all-or-nothing,
      // counting warm-ups — so any exercise the user cut short contributed ZERO
      // calories even though its completed sets were persisted and are counted
      // by the volume chart on the same screen. Cut every lift short and the
      // day's Energy Output bar read '--' for a workout that definitely
      // happened.
      //
      // The per-modality estimators below already filter per SET
      // (`ex.sets.filter(s => s.completed)`), so partial exercises are handled
      // correctly and an exercise with no completed sets contributes 0 anyway.
      exercises.push(...s.exercises);
    }
  } else if (sessionType === 'range') {
    // Range sessions: estimate from duration only
    const bwKg = lbsToKg(bodyWeightLbs || DEFAULT_BODY_WEIGHT_LBS);
    const durationMinutes = session.duration || 0;
    const rangeCals = Math.round(RANGE_MET * bwKg * (durationMinutes / 60));
    return {
      sessionId: session.id,
      sessionType,
      totalCalories: rangeCals,
      baseCalories: rangeCals,
      epocMultiplier: 1.0,
      exerciseCalories: [{
        exerciseName: 'Range Session',
        exerciseType: 'range',
        calories: rangeCals,
        method: 'met',
        metValue: RANGE_MET,
        durationSeconds: durationMinutes * 60,
        confidence: 'medium',
      }],
      avgConfidence: 'medium',
      bodyWeightLbs,
      formulaVersion: CALORIE_VERSION,
      calculatedAt: new Date(),
    };
  }

  const exerciseCalories = exercises.map(ex => estimateExerciseCalories(ex, bodyWeightLbs));
  const baseCalories = exerciseCalories.reduce((sum, ec) => sum + ec.calories, 0);

  // Calculate average RPE across all exercises for EPOC
  const allRPEs = exercises.map(ex => getExerciseRPE(ex));
  const avgRPE = allRPEs.length > 0
    ? allRPEs.reduce((a, b) => a + b, 0) / allRPEs.length
    : DEFAULT_RPE;

  // Apply EPOC multiplier
  const epocMultiplier = calculateEPOCMultiplier(exercises, avgRPE);
  const totalCalories = Math.round(baseCalories * epocMultiplier);

  // Average confidence
  const confidenceMap = { high: 3, medium: 2, low: 1 };
  const avgScore = exerciseCalories.length > 0
    ? exerciseCalories.reduce((sum, ec) => sum + confidenceMap[ec.confidence], 0) / exerciseCalories.length
    : 2;
  const avgConfidence: CalorieEstimate['confidence'] =
    avgScore >= 2.5 ? 'high' : avgScore >= 1.5 ? 'medium' : 'low';

  return {
    sessionId: session.id,
    sessionType,
    totalCalories,
    baseCalories,
    epocMultiplier,
    exerciseCalories,
    avgConfidence,
    bodyWeightLbs,
    formulaVersion: CALORIE_VERSION,
    calculatedAt: new Date(),
  };
}

// ============================================================================
// DAILY AGGREGATION
// ============================================================================

/**
 * Aggregate workout calories + NEAT into a daily summary.
 * neatCalories is a straight passthrough from DailyActivity.activeCalories.
 */
export function calculateDailyCalories(
  sessionSummaries: SessionCalorieSummary[],
  neatCalories: number,
  date?: Date
): DailyCalorieSummary {
  const workoutCalories = sessionSummaries.reduce((sum, s) => sum + s.totalCalories, 0);
  const hasWearableData = neatCalories > 0;
  const dateStr = (date || new Date()).toISOString().split('T')[0];

  return {
    date: dateStr,
    workoutCalories,
    neatCalories: hasWearableData ? neatCalories : 0,
    totalCalories: workoutCalories + (hasWearableData ? neatCalories : 0),
    sessions: sessionSummaries.map(s => ({
      sessionId: s.sessionId,
      sessionType: s.sessionType,
      calories: s.totalCalories,
    })),
    hasWearableData,
  };
}
